/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import { useRequest } from 'ahooks';
import { Grid, Header, Modal } from 'semantic-ui-react';
import SquareImage from '../../../components/SquareImage';
import { bem } from '../../../core/design/bem';
import { replaceValues } from '../../../utils/stringUtils';
import './UserProfileNetworkingModal.scss';
import ImageIcon from '../../../components/ImageIcon';
import platformService from '../../../core/services/platform.service';
import { PrivateMention } from '../UserProfileGrid/UserProfileGrid';

const css = bem('UserProfileNetworkingModal');
const translationPrefix = 'profile.networking.modal';

function useNetworkingParticipant(userId: string): any {
  return useRequest(() => platformService.fetchNetworkingParticipant(userId), {
    cacheKey: `networking/participant/${userId}`,
    cacheTime: 60 * 1000,
    refreshDeps: [userId],
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const UserProfileNetworkingModal = (props: any): JSX.Element | null => {
  const { userId, onClose, config = {} } = props;
  const {
    defaultThumbnail,
    roleTemplate = '{role}',
    companyTemplate = '{company}',
    fields = [],
  } = config;
  const { t } = useTranslation();
  const { data: user } = useNetworkingParticipant(userId);
  if (!user) return null;

  const { firstName, lastName, thumbnail = defaultThumbnail } = user;
  const role = replaceValues(roleTemplate, user);
  const company = replaceValues(companyTemplate, user);
  const values = fields.map((f: any) => ({ ...f, value: get(user, f.key) }));

  return (
    <Modal
      open
      onClose={() => {
        onClose({ userId: undefined, modal: undefined });
      }}
      closeIcon
    >
      <Modal.Header className={css('header').toString()}>
        {t(`${translationPrefix}.header`, user)}
      </Modal.Header>
      <Modal.Content scrolling className={css('container').toString()}>
        {thumbnail && (
          <SquareImage
            as="img"
            src={thumbnail}
            alt={`${firstName} ${lastName}`}
            className={css('avatar').toString()}
            imageSize={200}
            cdnOptions={{}}
          />
        )}
        <div className="info">
          <Header className="fullname">
            {firstName} {lastName}
          </Header>
          <div className="role">
            {(role || company) &&
              t(`${translationPrefix}.role-and-company`, { ...user, role, company })}
          </div>
          <Grid columns={2}>
            <Grid.Row className="group" style={{ paddingBottom: 0 }}>
              {values.map((field: any) => {
                const { key, icon, value } = field;
                return (
                  <Grid.Column key={key} className="information">
                    {icon && <ImageIcon icon={icon} maxHeight={20} style={{ marginRight: 10 }} />}
                    {value}
                    {!value && (
                      <PrivateMention
                        isPrivate
                        text={t(`${translationPrefix}.not-shared`)}
                        style={{ marginLeft: 0 }}
                      />
                    )}
                  </Grid.Column>
                );
              })}
            </Grid.Row>
          </Grid>
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default UserProfileNetworkingModal;
